import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useT } from '../lib/i18n';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DeleteAccountCard() {
  const { user, signOut } = useAuth();
  const { lang } = useT();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const requiredText = lang === 'fr' ? 'SUPPRIMER' : 'DELETE';
  const canDelete = confirmText.trim().toUpperCase() === requiredText;

  const performDelete = async () => {
    if (!canDelete || busy) return;
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(lang === 'fr' ? 'Session expirée. Reconnectez-vous.' : 'Session expired. Sign in again.');
        return;
      }
      await axios.post(`${API}/account/delete`, {
        user_id: user.id,
        access_token: session.access_token,
      });
      toast.success(lang === 'fr' ? '✓ Compte supprimé. Au revoir.' : '✓ Account deleted. Goodbye.');

      // Hard cleanup: local DBs, storage, then sign out and reload.
      // These best-effort cleanups must NOT throw — we silently log
      // failures to console for diagnostics but continue regardless.
      try {
        const dbs = await indexedDB.databases?.();
        for (const d of dbs || []) {
          if (d.name) indexedDB.deleteDatabase(d.name);
        }
      } catch (err) { console.warn('IndexedDB cleanup failed (non-blocking):', err); }
      try { await supabase.auth.signOut({ scope: 'local' }); }
      catch (err) { console.warn('SignOut cleanup failed (non-blocking):', err); }
      try { localStorage.clear(); }
      catch (err) { console.warn('localStorage cleanup failed (non-blocking):', err); }
      try { sessionStorage.clear(); }
      catch (err) { console.warn('sessionStorage cleanup failed (non-blocking):', err); }

      // Force full page reload to /auth so React state is wiped.
      setTimeout(() => { window.location.replace('/auth'); }, 600);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(lang === 'fr' ? `Échec : ${detail}` : `Failed: ${detail}`);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="card p-4 border-2 border-red-200 bg-red-50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 text-red-700 flex items-center justify-center shrink-0">
            <Trash2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-base text-red-900">
              {lang === 'fr' ? 'Supprimer mon compte' : 'Delete my account'}
            </div>
            <div className="text-xs text-red-800 mt-1 leading-relaxed">
              {lang === 'fr'
                ? 'Conformément à la Loi 25 du Québec (droit à l\'oubli). Cette action efface définitivement votre profil, vos soumissions, vos clients, vos photos et votre logo. Irréversible.'
                : 'Quebec Law 25 right-to-erasure. Permanently deletes your profile, quotes, clients, photos, and logo. Irreversible.'}
            </div>
            <button
              data-testid="open-delete-account"
              onClick={() => setOpen(true)}
              className="btn-outline gap-2 !min-h-[40px] !text-xs !text-red-700 !border-red-300 hover:!bg-red-100 mt-3"
            >
              <Trash2 size={14} /> {lang === 'fr' ? 'Demander la suppression' : 'Request deletion'}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="text-red-600" size={24} />
              <h2 className="font-heading font-black text-xl">
                {lang === 'fr' ? 'Êtes-vous absolument sûr ?' : 'Are you absolutely sure?'}
              </h2>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              {lang === 'fr'
                ? <>Vous êtes sur le point de supprimer définitivement votre compte <b>{user?.email}</b> ainsi que toutes les données associées :</>
                : <>You are about to permanently delete account <b>{user?.email}</b> and all associated data:</>}
            </p>
            <ul className="text-xs text-gray-700 space-y-1.5 mb-5 list-disc pl-5">
              <li>{lang === 'fr' ? 'Profil et logo' : 'Profile and logo'}</li>
              <li>{lang === 'fr' ? 'Toutes vos soumissions et signatures' : 'All quotes and signatures'}</li>
              <li>{lang === 'fr' ? 'Tous vos clients enregistrés' : 'All saved clients'}</li>
              <li>{lang === 'fr' ? 'Toutes les photos de chantier' : 'All site photos'}</li>
              <li>{lang === 'fr' ? 'Toutes les dépenses suivies' : 'All tracked expenses'}</li>
              <li>{lang === 'fr' ? 'Notifications push abonnées' : 'Push subscriptions'}</li>
            </ul>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-4">
              <div className="text-xs font-bold text-red-900 mb-1">
                {lang === 'fr' ? '⚠ Cette action est définitive' : '⚠ This action is permanent'}
              </div>
              <div className="text-[11px] text-red-800">
                {lang === 'fr'
                  ? 'Vos données seront effacées immédiatement. Aucune restauration possible.'
                  : 'Your data is erased immediately. No restoration possible.'}
              </div>
            </div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-600 block mb-2">
              {lang === 'fr'
                ? <>Tapez <span className="text-red-700">{requiredText}</span> pour confirmer</>
                : <>Type <span className="text-red-700">{requiredText}</span> to confirm</>}
            </label>
            <input
              data-testid="delete-account-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="field !border-red-300 mb-4"
              placeholder={requiredText}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setOpen(false); setConfirmText(''); }}
                disabled={busy}
                className="btn-outline flex-1"
              >
                {lang === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <button
                data-testid="confirm-delete-account"
                onClick={performDelete}
                disabled={!canDelete || busy}
                className="flex-1 min-h-[56px] rounded-xl px-6 font-bold text-base bg-red-700 text-white active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {lang === 'fr' ? 'Supprimer' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
